import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import axios from 'axios';
import authHeader from '../../services/auth-header';
import CommonServices from '../../services/common.service';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 400,
    paddingTop: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  submitButton: {
    marginTop: theme.spacing(1),
    alignSelf: 'flex-start',
  },
}));

const UserSettingsForm = () => {
  const classes = useStyles();
  const { user } = useSelector(state => state.auth);

  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');

    if (newPassword && newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword && newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }

    const body = {};
    if (username !== user?.username) body.username = username;
    if (email !== user?.email) body.email = email;
    if (newPassword) {
      body.currentPassword = currentPassword;
      body.newPassword = newPassword;
    }

    if (Object.keys(body).length === 0) {
      setError('No changes to save.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        CommonServices.getApiUrl() + 'auth/profile',
        body,
        { headers: authHeader() }
      );
      setSuccess(response.data.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Persist updated fields to localStorage so they survive page reload
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (response.data.username) stored.username = response.data.username;
      if (response.data.email) stored.email = response.data.email;
      localStorage.setItem('user', JSON.stringify(stored));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      {success && <Alert severity="success">{success}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Account Details
      </Typography>

      <TextField
        label="Username"
        value={username}
        onChange={e => setUsername(e.target.value)}
        variant="outlined"
        size="small"
        inputProps={{ minLength: 3, maxLength: 20 }}
      />
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        variant="outlined"
        size="small"
      />

      <Divider className={classes.divider} />
      <Typography variant="subtitle1">Change Password</Typography>

      <TextField
        label="Current Password"
        type="password"
        value={currentPassword}
        onChange={e => setCurrentPassword(e.target.value)}
        variant="outlined"
        size="small"
        helperText="Leave blank if not changing password"
      />
      <TextField
        label="New Password"
        type="password"
        value={newPassword}
        onChange={e => setNewPassword(e.target.value)}
        variant="outlined"
        size="small"
        inputProps={{ minLength: 6, maxLength: 40 }}
      />
      <TextField
        label="Confirm New Password"
        type="password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        variant="outlined"
        size="small"
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={loading}
        className={classes.submitButton}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
      >
        Save Changes
      </Button>
    </form>
  );
};

export default UserSettingsForm;
